/**
 * Sales records table (Sales records page) plus the shared SalesPager / SalesTableSkeleton
 * (also used by Purchases and SKU master; their props are unchanged).
 *
 * Revenue is whole rupees (en-IN, no paise); unit prices keep 2 decimals. Platforms show human
 * names from config/platforms.js. Sorting is server-side: `onSort(field)` toggles the field.
 * `onFilter(key, value)` (optional) makes the SKU and platform cells filter buttons.
 */
import React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { platformLabel } from '../../config/platforms';

const SALES_TABLE_GRID = '110px 140px minmax(200px, 1fr) 150px 80px 110px 120px 95px 120px';
const PAGE_SIZES = [50, 100, 200];

const badgeColor = platform => ({
  AMAZON_SELLER: 'bg-orange-100 text-orange-800', AMAZON_RETAIL: 'bg-slate-100 text-slate-800', FLIPKART: 'bg-blue-100 text-blue-800',
  FIRSTCRY: 'bg-pink-100 text-pink-800', MYNTRA: 'bg-red-100 text-red-800', NYKAA: 'bg-fuchsia-100 text-fuchsia-800',
  D2C_DROGO: 'bg-emerald-100 text-emerald-800', D2C_BAYBEE: 'bg-cyan-100 text-cyan-800', D2C_DOMESTICA: 'bg-violet-100 text-violet-800',
}[platform] || 'bg-gray-100 text-gray-700');

const unitPrice = (value, currency = '₹') => `${currency}${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const wholeRupees = (value, currency = '₹') => `${currency}${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayLabel = day => (typeof day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day)
  ? `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}` : (day || '—'));

const SortHeader = ({ field, label, align, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  const Icon = !active ? ArrowUpDown : sortOrder === 'desc' ? ArrowDown : ArrowUp;
  return (
    <div role="columnheader" aria-sort={active ? (sortOrder === 'desc' ? 'descending' : 'ascending') : 'none'} className={`flex ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
      <button type="button" className={`inline-flex w-full items-center gap-1 p-4 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'} ${active ? 'text-gray-900' : ''}`}
        onClick={() => onSort(field)} data-testid={`sort-sales-${field}-button`}>
        {label}<Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
};

const FilterLink = ({ onClick, label, children, className = '' }) => (onClick
  ? <button type="button" onClick={onClick} aria-label={label} title={label} className={`max-w-full truncate text-left hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${className}`}>{children}</button>
  : <span className={className}>{children}</span>);

const SaleRow = ({ sale, index, currency, onFilter }) => {
  const name = platformLabel(sale.platform || 'OTHERS');
  return (
    <div role="row" className="grid items-center border-b text-[13px] transition-colors hover:bg-gray-50" style={{ gridTemplateColumns: SALES_TABLE_GRID }} data-testid={`sales-row-${index}`}>
      <div role="cell" className="p-4 text-gray-600 tabular-nums">{dayLabel(sale.date)}</div>
      <div role="cell" className="min-w-0 p-4 font-mono text-[12px]">
        <FilterLink onClick={onFilter && sale.sku_code ? () => onFilter('sku', sale.sku_code) : null} label={`Show only ${sale.sku_code}`}>{sale.sku_code}</FilterLink>
      </div>
      <div role="cell" className="truncate p-4 text-gray-700" title={sale.product_name || ''}>{sale.product_name || '—'}</div>
      <div role="cell" className="min-w-0 p-4">
        <FilterLink onClick={onFilter ? () => onFilter('platform', sale.platform || 'OTHERS') : null} label={`Show only ${name}`}
          className={`inline-block rounded-md px-2 py-1 text-[11px] font-medium ${badgeColor(sale.platform)}`}>{name}</FilterLink>
      </div>
      <div role="cell" className="p-4 text-right tabular-nums">{Number(sale.units_sold || 0).toLocaleString('en-IN')}</div>
      <div role="cell" className="p-4 text-right tabular-nums text-gray-500">{sale.base_price ? unitPrice(sale.base_price, currency) : '—'}</div>
      <div role="cell" className="p-4 text-right tabular-nums font-medium">{unitPrice(sale.effective_price || sale.base_price, currency)}</div>
      <div role="cell" className="p-4 text-center">{sale.discount_pct > 0
        ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800">−{sale.discount_pct}%</span>
        : sale.discount_pct < 0 ? <span className="text-[11px] text-gray-500" title="Sold above list price">+{Math.abs(sale.discount_pct)}%</span>
          : <span className="text-[11px] text-gray-400">—</span>}</div>
      <div role="cell" className="p-4 text-right tabular-nums font-semibold text-gray-900">{wholeRupees(sale.revenue, currency)}</div>
    </div>
  );
};

export const SalesTableSkeleton = ({ rows = 8 }) => (
  <div data-testid="sales-table-skeleton">
    {Array.from({ length: rows }, (_, i) => <div key={`sk-${i}`} className="grid border-b p-4"><Skeleton className="h-5 w-full" /></div>)}
  </div>
);

export const SalesPager = ({ page, pageSize, total, onPage, onPageSize, loading, prefix = 'sales' }) => {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-gray-50/60 px-4 py-3 text-[12px] text-gray-600" data-testid={`${prefix}-pager`}>
      <span data-testid={`${prefix}-pager-range`}>{from.toLocaleString('en-IN')}–{to.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}</span>
      <div className="flex flex-wrap items-center gap-2">
        <span>Rows</span>
        <Select value={String(pageSize)} onValueChange={value => onPageSize(Number(value))}>
          <SelectTrigger className="h-8 w-[80px] text-[12px]" aria-label="Rows per page" data-testid={`${prefix}-page-size`}><SelectValue /></SelectTrigger>
          <SelectContent>{PAGE_SIZES.map(size => <SelectItem key={size} value={String(size)} data-testid={`${prefix}-page-size-${size}`}>{size}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8 px-2" aria-label="Previous page" disabled={loading || page <= 1} onClick={() => onPage(page - 1)} data-testid={`${prefix}-page-prev`}><ChevronLeft className="h-4 w-4" aria-hidden="true" /></Button>
        <span data-testid={`${prefix}-page-indicator`}>Page {page.toLocaleString('en-IN')} / {pages.toLocaleString('en-IN')}</span>
        <Button variant="outline" size="sm" className="h-8 px-2" aria-label="Next page" disabled={loading || page >= pages} onClick={() => onPage(page + 1)} data-testid={`${prefix}-page-next`}><ChevronRight className="h-4 w-4" aria-hidden="true" /></Button>
      </div>
    </div>
  );
};

export const DailySalesTable = ({ rows, loading, currency = '₹', sortBy, sortOrder, onSort, hasFilters, onReset, onFilter, canAdd = false, children }) => (
  <div className="relative overflow-x-auto" role="table" aria-label="Sales records" aria-busy={loading || undefined} data-testid="sales-table">
    <div className="min-w-[1145px]">
      <div className="grid border-b bg-gray-50 text-[13px] font-medium text-gray-600" style={{ gridTemplateColumns: SALES_TABLE_GRID }} role="row">
        <SortHeader field="date" label="Date" align="left" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <div role="columnheader" className="p-4 text-left">SKU</div>
        <div role="columnheader" className="p-4 text-left">Product</div>
        <div role="columnheader" className="p-4 text-left">Platform</div>
        <SortHeader field="units" label="Units" align="right" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <div role="columnheader" className="p-4 text-right">List price</div>
        <div role="columnheader" className="p-4 text-right">Selling price</div>
        <SortHeader field="discount" label="Discount" align="center" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
        <SortHeader field="revenue" label="Revenue" align="right" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
      </div>
      <div role="rowgroup" className={`transition-opacity ${loading && rows.length ? 'opacity-50' : ''}`}>
        {loading && !rows.length ? <SalesTableSkeleton /> : rows.map((sale, index) => <SaleRow key={sale.id || index} sale={sale} index={index} currency={currency} onFilter={onFilter} />)}
      </div>
    </div>
    {!loading && rows.length === 0 && (hasFilters
      ? <div className="py-12 text-center text-[14px] text-gray-500">No records match these filters. <button type="button" onClick={onReset} className="text-blue-600 hover:underline" data-testid="reset-empty-sales-filters-button">Clear filters</button> or pick a wider date range.</div>
      : <div className="py-12 text-center text-[14px] text-gray-500" data-testid="sales-empty">No sales records in this period. {canAdd ? 'Upload a sales file or add a record to get started.' : 'Pick a wider date range.'}</div>)}
    {children}
  </div>
);
